// L'argent du mois : revenus, dépenses, crédits, épargne automatique et intérêts.
import { HOUSING } from '../content/paths';
import { clamp, loanMonthlyPayment, round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, Loan } from '../engine/state';
import { dateAtDay, monthIndexAt } from '../engine/time';
import { addJournal, addNotice, chargeOverdraftFee, overdraftLimit } from './actions';
import { rejectionThreshold, schoolFeeRejected, spiralMonth } from './decouvert';
import { CAR, ageCar, depreciateCar } from './car';
import { colocMalus, parentsMalus } from './housing';
import { homeValue, mortgageFinished } from './property';
import { PROPERTIES } from '../content/property';
import { announceEurosRate, avAverageResult, avMonth, avValue, creditAvInterest } from './av';
import { peaAverageResult, peaMonth, peaValue } from './pea';
import { termDepositsMonthlyIncome, termDepositsValue } from './termDeposit';
import {
  LDDS,
  LEP,
  LIVRET_A,
  creditYearlyInterest,
  depositToLdds,
  depositToLep,
  depositToLivret,
  depositToSavings,
  lepEligible,
  lepIncomeCeiling,
  lepTaxableIncome,
  savingsRoom,
  onSavingsWithdrawal,
} from './livret';

export const MONTHLY_HOURS = 151.67;
export const OVERTIME_BONUS = 1.25;
export const LIVRET_RATE = LIVRET_A.rate;
export const INFLATION_RATE = 0.02;
export const EMERGENCY_MONTHS = 3;
/** L'école privée des études longues : ce qu'elle prélève chaque octobre. */
export const SCHOOL_FEE = 5000;

export function fixedExpenses(state: GameState): number {
  return Object.values(state.expenses).reduce((sum, expense) => sum + (expense?.amount ?? 0), 0);
}

/** Dépenses du mois hors crédits : dépenses fixes et budget d'envies. */
export function monthlySpending(state: GameState): number {
  return fixedExpenses(state) + state.budget.leisure;
}

/** Pendant les études longues, dans une école privée. */
export function inPrivateSchool(state: GameState): boolean {
  return state.path === 'studies' && state.stage === 'prologue' && state.prologueStep === 'years' && !state.flags.excluEcole;
}

/** Les changements de mois d'ici le premier emploi : autant de mois où une dette en différé grossit. */
export function monthsUntilFirstJob(state: GameState): number {
  if (state.firstJobTargetDay === null) return 0;
  return Math.max(0, monthIndexAt(state.startDate, state.firstJobTargetDay) - monthIndexAt(state.startDate, state.day));
}

/** Une dette en différé total : ses intérêts s'y ajoutent chaque mois. */
export function deferredDebt(amount: number, annualRate: number, months: number): number {
  return amount * (1 + annualRate / 12) ** months;
}

/** Ce qui a été emprunté en prêts étudiants, ceux qui se remboursent à partir du premier emploi. */
export function studentLoansTotal(state: GameState): number {
  return state.loans.filter((loan) => loan.deferredUntilJob).reduce((sum, loan) => sum + loan.principal, 0);
}

/** La mensualité prévue d'un prêt en différé, sur la dette qu'il aura au premier emploi. */
export function deferredLoanPayment(state: GameState, loan: Loan): number {
  const debt = deferredDebt(loan.balance, loan.annualRate, monthsUntilFirstJob(state));
  return round2(loanMonthlyPayment(debt, loan.annualRate, loan.monthsLeft));
}

/** Au premier emploi, les prêts en différé commencent : leurs mensualités se calculent sur la dette grossie. */
export function startDeferredLoans(state: GameState): void {
  for (const loan of state.loans) {
    if (!loan.deferredUntilJob) continue;
    loan.monthlyPayment = round2(loanMonthlyPayment(loan.balance, loan.annualRate, loan.monthsLeft));
    loan.totalInterest = round2(loan.monthlyPayment * loan.monthsLeft - loan.principal);
    addJournal(state, 'journal.studentLoanStarts', {
      label: t(loan.label),
      balance: fmtEur(loan.balance),
      payment: fmtEur(loan.monthlyPayment),
      months: loan.monthsLeft,
    });
  }
}

/** L'école privée prélève l'année en octobre ; la banque rejette si le compte s'enfonçait trop. */
function chargeSchoolFees(state: GameState): void {
  if (state.flags.ecoleImpayee) return;
  const fee = atTodayPrices(state, SCHOOL_FEE);
  if (state.cash - fee < -(overdraftLimit(state) + rejectionThreshold(state))) {
    schoolFeeRejected(state, fee);
    return;
  }
  state.cash = round2(state.cash - fee);
  const params = { amount: fmtEur(fee) };
  addNotice(state, 'school.fees', params);
  addJournal(state, 'school.fees', params);
}

export function isLoanActive(state: GameState, loan: Loan): boolean {
  return loan.monthsLeft > 0 && (!loan.deferredUntilJob || state.firstJobDay !== null);
}

export function monthlyLoanPayments(state: GameState): number {
  return state.loans
    .filter((loan) => isLoanActive(state, loan))
    .reduce((sum, loan) => sum + loan.monthlyPayment + (loan.insuranceMonthly ?? 0), 0);
}

export function monthlyIncome(state: GameState): number {
  if (state.stage !== 'prologue') return state.salary;
  if (state.prologueStep === 'years') return state.studentIncome + state.parentsHelp;
  return 0;
}

export function emergencyTarget(state: GameState): number {
  return EMERGENCY_MONTHS * monthlySpending(state);
}

/** Le Livret A s'ouvre avec le premier compte, à la fin du prologue. */
export function livretOpen(state: GameState): boolean {
  return state.stage !== 'prologue' || state.flags.livretOpen === true;
}

/** Ce que rapportent chaque mois les livrets et les comptes à terme (nets d'impôt) : un revenu régulier. */
export function steadyIncome(state: GameState): number {
  return (
    (state.livret * LIVRET_A.rate + state.ldds * LDDS.rate + state.lep * LEP.rate) / 12 +
    termDepositsMonthlyIncome(state)
  );
}

/** Ce que les placements (PEA et assurance-vie) ont fait gagner ou perdre le mois écoulé, frais compris. */
export function placementsResult(state: GameState): number {
  return state.pea.lastMonthResult + state.av.lastMonthResult;
}

/**
 * Les revenus passifs du mois : les intérêts des livrets et des comptes à terme, plus ce que les placements
 * ont fait gagner ou perdre le mois écoulé. Ils deviennent négatifs quand la bourse baisse.
 */
export function passiveIncome(state: GameState): number {
  return steadyIncome(state) + placementsResult(state);
}

/** Ce que les placements ouverts ont gagné ou perdu depuis leur ouverture, frais compris, et ce qui y a été versé. */
export function placementsSinceOpening(state: GameState): { gain: number; deposits: number } {
  let value = 0;
  let deposits = 0;
  if (state.pea.open) {
    value += peaValue(state);
    deposits += state.pea.deposits;
  }
  if (state.av.open) {
    value += avValue(state);
    deposits += state.av.deposits;
  }
  return { gain: round2(value - deposits), deposits: round2(deposits) };
}

/** Ce que les placements ont fait gagner ou perdre en moyenne chaque mois sur un an : la tendance de la bourse. */
export function placementsAverageResult(state: GameState): number {
  return peaAverageResult(state) + avAverageResult(state);
}

/** Les mêmes revenus, avec la moyenne des gains et des pertes de la bourse sur un an : la tendance. */
export function passiveIncomeAverage(state: GameState): number {
  return steadyIncome(state) + placementsAverageResult(state);
}

export function outstandingDebt(state: GameState): number {
  return state.loans.reduce((sum, loan) => sum + loan.balance, 0);
}

/** Le patrimoine net compte la voiture à sa valeur de revente. */
export function networth(state: GameState): number {
  return (
    state.cash +
    state.piggy +
    state.livret +
    state.ldds +
    state.lep +
    peaValue(state) +
    avValue(state) +
    termDepositsValue(state) +
    state.carValue +
    homeValue(state) +
    state.accruedInterest +
    state.lddsAccrued +
    state.lepAccrued -
    state.accruedAgios -
    outstandingDebt(state)
  );
}

/**
 * Un prix de départ, mis au niveau des prix du moment : +2 % à chaque bilan annuel.
 * Les montants fixés par la loi (amendes, taxes, indemnités) ne passent pas par là.
 */
export function atTodayPrices(state: GameState, amount: number): number {
  return Math.round(amount * state.priceLevel);
}

/**
 * Une rentrée d'argent de l'année : salaire, heures en extra, prime, gain d'un événement.
 * Le taux d'épargne du bilan annuel se calcule sur toutes ces rentrées.
 */
export function recordIncome(state: GameState, amount: number): void {
  if (amount <= 0) return;
  state.year.income += amount;
  // L'année civile aussi : c'est elle que regarde l'administration fiscale (le droit au LEP).
  state.taxYearIncome += amount;
}

export interface MonthReport {
  income: number;
  saved: number;
  interest: number;
  negative: boolean;
}

export function processMonth(state: GameState): MonthReport {
  // Un nouveau mois commence : le plafond des frais de découvert repart à zéro.
  state.overdraftFeesThisMonth = 0;
  const income = monthlyIncome(state);
  state.cash += income;
  const beforeBills = state.cash;
  state.cash -= monthlySpending(state);

  // Pendant un plan de surendettement, les mensualités sont suspendues : la banque prélève, elle (decouvert.ts).
  // Le crédit de la résidence principale continue : c'est lui qui garde le toit.
  for (const loan of state.loans) {
    if (state.surendettement && !loan.mortgage) continue;
    if (!isLoanActive(state, loan)) {
      // Un prêt en différé total : pendant les études, ses intérêts s'ajoutent à ce qui est dû.
      if (loan.deferredUntilJob) loan.balance = round2(loan.balance * (1 + loan.annualRate / 12));
      continue;
    }
    const interest = (loan.balance * loan.annualRate) / 12;
    loan.balance = round2(Math.max(0, loan.balance - (loan.monthlyPayment - interest)));
    loan.monthsLeft -= 1;
    state.cash -= loan.monthlyPayment + (loan.insuranceMonthly ?? 0);
    if (loan.monthsLeft === 0) {
      loan.balance = 0;
      addJournal(state, 'journal.loanPaid', { label: t(loan.label) });
      if (loan.mortgage) mortgageFinished(state);
    }
  }
  state.loans = state.loans.filter((loan) => loan.monthsLeft > 0);
  if (dateAtDay(state.startDate, state.day).getMonth() === 9 && inPrivateSchool(state)) chargeSchoolFees(state);

  // Les agios calculés jour après jour pendant le mois sont prélevés maintenant.
  const agios = round2(state.accruedAgios);
  state.cash -= agios;
  state.accruedAgios = 0;
  // Si les factures font passer au-delà du découvert autorisé, des frais s'ajoutent.
  chargeOverdraftFee(state, beforeBills);

  let saved = 0;
  if (state.budget.autoSaving > 0) {
    const transfer = Math.min(state.budget.autoSaving, Math.max(0, state.cash));
    if (livretOpen(state)) {
      // Le Livret A d'abord, puis le LDDS : ce qui ne rentre plus sous les plafonds reste sur le compte courant.
      saved = depositToSavings(state, transfer);
      if (saved < transfer) warnLivretFull(state);
    } else {
      saved = transfer;
      state.piggy += saved;
    }
    state.cash -= saved;
  }
  if (savingsRoom(state) >= LIVRET_A.minOperation) state.flags.livretFull = false;
  // Le PEA : le résultat du mois écoulé, puis le versement programmé. L'assurance-vie : ses frais et son résultat.
  peaMonth(state);

  // Les intérêts du Livret A sont versés une fois par an, le 31 décembre.
  const january = dateAtDay(state.startDate, state.day).getMonth() === 0;
  const interest = january ? creditYearlyInterest(state) : 0;
  if (january) {
    closeTaxYear(state);
    // L'assurance-vie : les intérêts de l'année passée, puis le taux de la nouvelle.
    creditAvInterest(state);
    announceEurosRate(state);
  }
  avMonth(state);

  // La spirale du découvert : rejets, convocation, fichage, plan (voir decouvert.ts).
  spiralMonth(state);

  const negative = state.cash < 0;
  depreciateCar(state);
  ageCar(state);

  state.cash = round2(state.cash);
  state.livret = round2(state.livret);
  state.piggy = round2(state.piggy);
  updateWellbeing(state, negative);
  state.depletionsThisMonth = 0;
  return { income, saved, interest, negative };
}

/** Le bien-être se rapproche chaque mois d'une cible qui dépend des envies, du logement et du stress. */
function updateWellbeing(state: GameState, negative: boolean): void {
  const income = monthlyIncome(state);
  // Un abonnement de loisir compte comme des envies, et apporte son propre bonus.
  const perks = Object.values(state.perks);
  const perkLeisure = perks.reduce((sum, perk) => sum + perk.leisure, 0);
  // La hausse des prix rogne le plaisir : à budget égal, on s'offre un peu moins chaque année.
  const realLeisure = (state.budget.leisure + perkLeisure) / state.priceLevel;
  const leisureRatio = income > 0 ? realLeisure / income : 0;
  let target = 40 + Math.min(35, leisureRatio * 120);
  if (state.housing) target += state.home ? PROPERTIES[state.home.kind].wellbeing : HOUSING[state.housing].wellbeingBonus;
  if (state.hasCar) target += CAR.wellbeing + (state.carNew ? CAR.newWellbeing : 0);
  target += perks.reduce((sum, perk) => sum + perk.wellbeing, 0);
  // Rester trop longtemps chez ses parents pèse de plus en plus ; une colocation qui dure aussi, plus doucement.
  target -= parentsMalus(state);
  target -= colocMalus(state);
  if (state.day < state.fatigueUntilDay) target -= 15;
  if (negative) target -= 10;
  // Sous la cible, le moral se répare deux fois plus vite qu'il ne s'use au-dessus : sans ça, chaque
  // point perdu régulièrement (heures en extra, coups durs) pèserait quatre fois son montant à l'arrivée.
  const pull = state.wellbeing < target ? 0.5 : 0.25;
  state.wellbeing = clamp(state.wellbeing + (target - state.wellbeing) * pull, 0, 100);
}

/**
 * En janvier, l'année civile se referme : ses revenus deviennent ceux de « l'an dernier ».
 * La banque vérifie alors le droit au LEP : au-dessus du plafond deux années de suite, il est fermé.
 */
function closeTaxYear(state: GameState): void {
  state.lastTaxYearIncome = round2(state.taxYearIncome);
  state.taxYearIncome = 0;
  if (!state.lepOpen) return;
  if (lepTaxableIncome(state) <= lepIncomeCeiling(state)) {
    state.lepOverYears = 0;
    return;
  }
  state.lepOverYears += 1;
  const params = { income: fmtEur(lepTaxableIncome(state)), ceiling: fmtEur(lepIncomeCeiling(state)) };
  if (state.lepOverYears < 2) {
    addNotice(state, 'lep.overOnce', params);
    addJournal(state, 'lep.overOnce', params);
    return;
  }
  closeLep(state);
}

/** Le LEP est fermé : son argent passe sur le Livret A, puis le LDDS, et le reste sur le compte courant. */
function closeLep(state: GameState): void {
  const amount = round2(state.lep);
  state.lepOpen = false;
  state.lep = 0;
  state.lepBase = 0;
  state.lepOverYears = 0;
  const deposited = depositToSavings(state, amount);
  state.cash = round2(state.cash + amount - deposited);
  addNotice(state, 'lep.closed', { amount: fmtEur(amount) });
  addJournal(state, 'lep.closed', { amount: fmtEur(amount) });
}

/** Le virement automatique ne rentre plus : on le signale une fois, jusqu'à ce qu'il y ait de nouveau de la place. */
function warnLivretFull(state: GameState): void {
  if (state.flags.livretFull) return;
  state.flags.livretFull = true;
  const key = state.lddsOpen ? 'savings.full' : 'livret.full';
  addNotice(state, key);
  addJournal(state, key);
}

/** Un versement sur le Livret A : au moins 10 €, et dans la limite du plafond. */
export function transferToLivret(state: GameState, amount: number): void {
  const value = Math.min(amount, Math.max(0, state.cash));
  if (value < LIVRET_A.minOperation || !livretOpen(state)) return;
  const deposited = depositToLivret(state, value);
  state.cash = round2(state.cash - deposited);
  state.year.saved += deposited;
}

export function withdrawFromLivret(state: GameState, amount: number): void {
  const value = Math.min(amount, state.livret);
  if (value < LIVRET_A.minOperation) return;
  state.livret = round2(state.livret - value);
  state.cash = round2(state.cash + value);
  state.year.saved -= value;
  onSavingsWithdrawal(state);
}

export function emptyPiggy(state: GameState): void {
  if (state.piggy <= 0 || !livretOpen(state)) return;
  const deposited = depositToSavings(state, state.piggy);
  state.piggy = round2(state.piggy - deposited);
  state.year.saved += deposited;
}

/** Ouvrir un LDDS : un premier versement de 15 €, pris sur le compte courant. */
export function openLdds(state: GameState): void {
  if (state.lddsOpen || !livretOpen(state) || state.cash < LDDS.minOperation) return;
  state.lddsOpen = true;
  state.cash = round2(state.cash - LDDS.minOperation);
  state.ldds = LDDS.minOperation;
  state.year.saved += LDDS.minOperation;
  addJournal(state, 'journal.lddsOpen');
}

/** Un versement sur le LDDS : au moins 15 €, et dans la limite du plafond. */
export function transferToLdds(state: GameState, amount: number): void {
  const value = Math.min(amount, Math.max(0, state.cash));
  if (value < LDDS.minOperation || !state.lddsOpen) return;
  const deposited = depositToLdds(state, value);
  state.cash = round2(state.cash - deposited);
  state.year.saved += deposited;
}

export function withdrawFromLdds(state: GameState, amount: number): void {
  const value = Math.min(amount, state.ldds);
  if (value < LDDS.minOperation) return;
  state.ldds = round2(state.ldds - value);
  state.cash = round2(state.cash + value);
  state.year.saved -= value;
  onSavingsWithdrawal(state);
}

/** Ouvrir un LEP : il faut y avoir droit, et un premier versement de 30 €, pris sur le compte courant. */
export function openLep(state: GameState): void {
  if (state.lepOpen || !lepEligible(state) || state.cash < LEP.minOpening) return;
  state.lepOpen = true;
  state.lepOverYears = 0;
  state.cash = round2(state.cash - LEP.minOpening);
  state.lep = LEP.minOpening;
  state.year.saved += LEP.minOpening;
  addJournal(state, 'journal.lepOpen');
}

/** Un versement sur le LEP : au moins 10 €, et dans la limite du plafond. */
export function transferToLep(state: GameState, amount: number): void {
  const value = Math.min(amount, Math.max(0, state.cash));
  if (value < LEP.minOperation || !state.lepOpen) return;
  const deposited = depositToLep(state, value);
  state.cash = round2(state.cash - deposited);
  state.year.saved += deposited;
}

export function withdrawFromLep(state: GameState, amount: number): void {
  const value = Math.min(amount, state.lep);
  if (value < LEP.minOperation) return;
  state.lep = round2(state.lep - value);
  state.cash = round2(state.cash + value);
  state.year.saved -= value;
  onSavingsWithdrawal(state);
}

/** Verser tout le compte courant sur les livrets : le LEP d'abord, puis le Livret A, puis le LDDS. */
export function transferAllToSavings(state: GameState): void {
  const value = Math.max(0, state.cash);
  if (value < LIVRET_A.minOperation || !livretOpen(state)) return;
  const deposited = depositToSavings(state, value);
  state.cash = round2(state.cash - deposited);
  state.year.saved += deposited;
}
